import type { ApiError } from '@/lib/api/errors';

function responseData(err: unknown): unknown {
  const apiError = err as Partial<ApiError>;
  if (apiError.data) return apiError.data;
  return (err as { response?: { data?: unknown } })?.response?.data;
}

export function crewErrorCode(err: unknown): string | undefined {
  const data = responseData(err) as { code?: unknown } | undefined;
  return typeof data?.code === 'string' ? data.code : undefined;
}

/** Why a visit booked to its day could not move to `day` (already a label). */
export function describeDayMoveError(err: unknown, day: string, technicianName?: string): string {
  const data = responseData(err) as
    | { code?: string; message?: unknown; conflicts?: Array<{ type?: string }> }
    | undefined;
  const who = technicianName ?? 'The technician';
  // Only a full day asks for the day to itself.
  if (data?.conflicts?.some((conflict) => conflict.type === 'TECH_DOUBLE_BOOKED'))
    return `${who} already has visits on ${day}, and a full day needs the day free. Choose another day.`;
  switch (data?.code) {
    case 'NO_SHIFT_ON_DATE':
      return `${who} doesn't work on ${day}. Choose another day.`;
    case 'NO_FEASIBLE_PLAN':
      return `${who} has no room for this visit on ${day}. Choose another day or technician.`;
    case 'WINDOW_CAPACITY_EXCEEDED':
      return `${day} is fully booked. Choose another day.`;
    default:
      return typeof data?.message === 'string' ? data.message : 'Could not move the visit.';
  }
}

export function describeCrewError(err: unknown): string {
  const data = responseData(err) as
    | { code?: string; details?: { minCrewSize?: number } }
    | undefined;

  switch (data?.code) {
    case 'CANNOT_REPLACE_LEAD_IN_PROGRESS':
      return "Can't change the lead while work is in progress. Cancel this visit and create a new one.";
    case 'BELOW_MIN_CREW_SIZE': {
      const required = data.details?.minCrewSize ?? 'the required number of';
      return `This job needs at least ${required} technicians. Add a helper before saving.`;
    }
    case 'PLAN_OUTSIDE_CONSTRAINTS':
      return 'That day is outside the dates this visit is due. Pick another day.';
    default:
      return 'Failed to update the crew.';
  }
}
